// frontend/src/components/ui/enhanced-form-validation.jsx
import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';
import { getInputClasses } from '../../utils/brandingUtils';
import {
    ExclamationTriangleIcon,
    CheckCircleIcon,
    InformationCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

// Helper to get nested value from object using dot notation
const getNestedValue = (obj, path) => {
    if (!path) return undefined;
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
        if (value == null || typeof value !== 'object') return undefined;
        value = value[key];
    }
    return value;
};

// Helper to set nested value in object using dot notation
const setNestedValue = (obj, path, value) => {
    if (!path) return obj;
    const keys = path.split('.');
    const newObj = { ...obj };
    let current = newObj;
    
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (current[key] == null || typeof current[key] !== 'object') {
            current[key] = {};
        } else {
            current[key] = { ...current[key] };
        }
        current = current[key];
    }
    
    current[keys[keys.length - 1]] = value;
    return newObj;
};

// Enhanced Form Validation Hook
export const useFormValidation = (initialData = {}, validationRules = {}) => {
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showValidationSummary, setShowValidationSummary] = useState(false);

    // Real-time validation
    const validateField = (field, value) => {
        const rule = validationRules[field];
        if (!rule) return null;

        // Handle nested paths - get value if not provided
        const fieldValue = value !== undefined ? value : getNestedValue(formData, field);

        // Check if field is required and empty
        if (rule.required) {
            // Handle different types: string, number, object, array
            if (fieldValue == null || fieldValue === '') {
                return rule.requiredMessage || `${field} is required`;
            }
            // For strings, also check if trimmed is empty
            if (typeof fieldValue === 'string' && fieldValue.trim() === '') {
                return rule.requiredMessage || `${field} is required`;
            }
        }

        if (rule.pattern && fieldValue && !rule.pattern.test(fieldValue)) {
            return rule.patternMessage || `${field} format is invalid`;
        }

        if (rule.minLength && fieldValue && fieldValue.length < rule.minLength) {
            return rule.minLengthMessage || `${field} must be at least ${rule.minLength} characters`;
        }

        if (rule.maxLength && fieldValue && fieldValue.length > rule.maxLength) {
            return rule.maxLengthMessage || `${field} must be no more than ${rule.maxLength} characters`;
        }

        if (rule.custom && fieldValue) {
            return rule.custom(fieldValue);
        }

        return null;
    };

    // Validate all fields
    const validateAll = () => {
        const newErrors = {};
        Object.keys(validationRules).forEach(field => {
            const fieldValue = getNestedValue(formData, field);
            const error = validateField(field, fieldValue);
            if (error) {
                newErrors[field] = error;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Update form data and validate
    const updateField = (field, value) => {
        // Handle nested paths
        if (field.includes('.')) {
            setFormData(prev => setNestedValue(prev, field, value));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
        
        // Mark field as touched
        setTouched(prev => ({ ...prev, [field]: true }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
        
        // Real-time validation for touched fields
        if (touched[field]) {
            const error = validateField(field, value);
            if (error) {
                setErrors(prev => ({ ...prev, [field]: error }));
            }
        }
    };

    // Get field error
    const getFieldError = (field) => {
        return touched[field] ? errors[field] : null;
    };

    // Check if form has errors
    const hasErrors = Object.values(errors).some(error => error !== null);

    // Get error count
    const errorCount = Object.values(errors).filter(error => error !== null).length;

    return {
        formData,
        errors,
        touched,
        showValidationSummary,
        setShowValidationSummary,
        updateField,
        validateAll,
        getFieldError,
        hasErrors,
        errorCount,
        setFormData,
        setErrors
    };
};

// Validation Summary Component
export const ValidationSummary = ({ 
    errors, 
    isVisible, 
    onClose, 
    className = "",
    title = "Please fix the following errors:",
    showCount = true
}) => {
    const errorEntries = Object.entries(errors).filter(([_, error]) => error !== null);
    
    if (!isVisible || errorEntries.length === 0) return null;

    return (
        <div className={cn(
            "bg-red-50 border border-red-200 rounded-lg p-4 mb-6",
            className
        )}>
            <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-sm font-medium text-red-800">
                            {title}
                            {showCount && (
                                <span className="ml-1 text-red-600">
                                    ({errorEntries.length} error{errorEntries.length !== 1 ? 's' : ''})
                                </span>
                            )}
                        </h3>
                        <ul className="mt-2 space-y-1">
                            {errorEntries.map(([field, error]) => (
                                <li key={field} className="text-sm text-red-700 flex items-center">
                                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>
                                    {error}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-red-400 hover:text-red-600 transition-colors"
                        aria-label="Close validation summary"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

// Enhanced Input Component with Validation
export const ValidatedInput = ({
    label,
    field,
    value,
    onChange,
    error,
    touched,
    type = "text",
    placeholder,
    required = false,
    className = "",
    ...props
}) => {
    const { branding } = useBranding();
    const hasError = touched && error;
    const inputClasses = getInputClasses(branding, hasError);
    
    return (
        <div className={cn("space-y-1", className)}>
            <label className="block text-sm font-medium text-foreground">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                placeholder={placeholder}
                className={cn(inputClasses)}
                {...props}
            />
            {hasError && (
                <p className="text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                    {error}
                </p>
            )}
        </div>
    );
};

// Enhanced Textarea Component with Validation
export const ValidatedTextarea = ({
    label,
    field,
    value,
    onChange,
    error,
    touched,
    placeholder,
    required = false,
    rows = 3,
    className = "",
    ...props
}) => {
    const { branding } = useBranding();
    const hasError = touched && error;
    const inputClasses = getInputClasses(branding, hasError);
    
    return (
        <div className={cn("space-y-1", className)}>
            <label className="block text-sm font-medium text-foreground">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className={cn(
                    "min-h-[80px] resize-vertical",
                    inputClasses
                )}
                {...props}
            />
            {hasError && (
                <p className="text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                    {error}
                </p>
            )}
        </div>
    );
};

// Enhanced Select Component with Validation
export const ValidatedSelect = ({
    label,
    field,
    value,
    onChange,
    error,
    touched,
    options = [],
    placeholder = "Select an option",
    required = false,
    className = "",
    ...props
}) => {
    const hasError = touched && error;
    
    return (
        <div className={cn("space-y-1", className)}>
            <label className="block text-sm font-medium text-foreground">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                className={cn(
                    "block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors",
                    hasError
                        ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                        : "border-input focus:ring-ring focus:border-ring",
                    className
                )}
                {...props}
            >
                <option value="">{placeholder}</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {hasError && (
                <p className="text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                    {error}
                </p>
            )}
        </div>
    );
};

// Form Progress Indicator
export const FormProgress = ({ 
    completedFields = 0, 
    totalFields = 0, 
    className = "" 
}) => {
    const percentage = totalFields > 0 ? (completedFields / totalFields) * 100 : 0;
    
    return (
        <div className={cn("space-y-2", className)}>
            <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Form Progress</span>
                <span className="text-foreground font-medium">
                    {completedFields}/{totalFields} fields completed
                </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
                <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
};

// Floating Validation Alert
export const FloatingValidationAlert = ({ 
    isVisible, 
    errorCount, 
    onDismiss,
    className = ""
}) => {
    if (!isVisible || errorCount === 0) return null;

    return (
        <div className={cn(
            "fixed bottom-4 right-4 z-50 max-w-sm",
            className
        )}>
            <div className="bg-red-600 text-white rounded-lg shadow-lg p-4">
                <div className="flex items-start space-x-3">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium">
                            {errorCount} validation error{errorCount !== 1 ? 's' : ''} found
                        </p>
                        <p className="text-xs text-red-100 mt-1">
                            Please fix the errors before submitting
                        </p>
                    </div>
                    {onDismiss && (
                        <button
                            onClick={onDismiss}
                            className="text-red-200 hover:text-white transition-colors"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Validation Rules Helper
export const createValidationRules = (rules) => {
    const defaultRules = {
        required: true,
        requiredMessage: 'This field is required',
        patternMessage: 'Invalid format',
        minLengthMessage: 'Too short',
        maxLengthMessage: 'Too long'
    };

    return Object.keys(rules).reduce((acc, field) => {
        acc[field] = { ...defaultRules, ...rules[field] };
        return acc;
    }, {});
};

// Common validation patterns
export const VALIDATION_PATTERNS = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^\+[1-9]\d{1,14}$/,
    url: /^https?:\/\/.+/,
    name: /^[a-zA-Z\s'-]+$/,
    companyName: /^[a-zA-Z0-9\s&.,'-]+$/
};

export default {
    useFormValidation,
    ValidationSummary,
    ValidatedInput,
    ValidatedTextarea,
    ValidatedSelect,
    FormProgress,
    FloatingValidationAlert,
    createValidationRules,
    VALIDATION_PATTERNS
};
